"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/observability/client-error-reporter";

/**
 * Global error handler that catches unhandled errors and promise rejections.
 * Placed at the root of the app. Reports each to /api/errors/client, which
 * logs via @wtc/logger → #Hub Errors Slack channel (employee sessions only).
 */
export function GlobalErrorHandler() {
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (process.env.NODE_ENV === "development") {
        console.error("Global unhandled error:", event);
      }
      reportClientError({
        message: event.message || "window.onerror",
        name: event.error?.name,
        stack: event.error?.stack,
        url: event.filename || window.location.href,
        context: { line: event.lineno, col: event.colno },
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as
        | { message?: string; name?: string; stack?: string }
        | undefined;
      if (process.env.NODE_ENV === "development") {
        console.error("Global unhandled promise rejection:", event.reason);
      }
      reportClientError({
        message: reason?.message || String(event.reason) || "unhandledrejection",
        name: reason?.name ?? "UnhandledRejection",
        stack: reason?.stack,
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
}
